/**
 * IdMap is a thin wrapper around the standard JavaScript `Map` object,
 * with additional safeguards and conveniences tailored for ID-based lookups.
 *
 * ## Motivation
 * - `Record<Id, Value>` has limitations because it does not distinguish between missing keys
 *   and keys with `undefined` values; accessing a missing key will not throw.
 * - `Map<Id, Value>` is better, but in most use cases we need to do `if (value !== undefined)` check
 *   and throw an error. IdMap addresses this issue with getOrThrow() method.
 */
export class IdMap<Id, Value> {
    /**
     * Internal storage for key-value pairs.
     */
    private inner: Map<Id, Value>;

    /**
     * A descriptive name of the collection used in error messages for easier debugging.
     */
    private name: string;

    /**
     * Creates a new `IdMap` instance with the given name.
     *
     * @param name - A name for the map, used in error messages.
     */
    constructor(name: string) {
        this.name = name;
        this.inner = new Map<Id, Value>();
    }

    /**
     * Creates a new `IdMap` from an array of items by extracting the ID and value from each item.
     */
    static fromArray<Id, Value, T>({
        name,
        items,
        getId,
        getValue,
    }: FromArrayArgs<Id, Value, T>): IdMap<Id, Value> {
        const idMap = new IdMap<Id, Value>(name);
        for (const item of items) {
            idMap.set(getId(item), getValue(item));
        }
        return idMap;
    }

    /**
     * Sets a value under the specified ID.
     */
    set(id: Id, value: Value): void {
        this.inner.set(id, value);
    }

    /**
     * Retrieves the value associated with the given ID, or `undefined` if not found.
     */
    get(id: Id): Value | undefined {
        return this.inner.get(id);
    }

    /**
     * Retrieves the value associated with the given ID, or throws an error if the ID is not found.
     */
    getOrThrow(id: Id): Value {
        const value = this.inner.get(id);
        if (value === undefined) {
            throw new Error(`IdMap ${this.name} has no item with id ${id}`);
        }
        return value;
    }
}

/**
 * Named arguments for `IdMap.fromArray` method.
 */
interface FromArrayArgs<Id, Value, T> {
    /**
     * A descriptive name used for debugging and error messages.
     */
    name: string;

    /**
     * The array of items to populate the map from.
     */
    items: Array<T>;

    /**
     * Function to extract the ID from an item.
     */
    getId: (item: T) => Id;

    /**
     * Function to extract the value from an item.
     */
    getValue: (item: T) => Value;
}
